/**
 * Impersonation context — allows superadmin to preview the app as a LOWER role.
 * Full-stack: apiClient sends X-Impersonate-Role on every request and the
 * backend downgrades req.user.role accordingly (identity and audit stamping
 * stay the real superadmin — see impersonationMiddleware and RBAC.md).
 * Persists to sessionStorage (clears on tab close).
 */
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import type { AppRole } from './usePermissions';
import { IMPERSONATE_ROLE_STORAGE_KEY, ROLE_RANK } from '../constants/roles';

interface ImpersonationContextValue {
  /** The role being impersonated, or null if not impersonating */
  impersonatingAs: AppRole | null;
  /** Start viewing as a different role */
  startImpersonation: (role: AppRole) => void;
  /** Stop impersonating — return to actual role */
  stopImpersonation: () => void;
  /** Whether impersonation is active */
  isImpersonating: boolean;
}

const ImpersonationContext = createContext<ImpersonationContextValue>({
  impersonatingAs: null,
  startImpersonation: () => {},
  stopImpersonation: () => {},
  isImpersonating: false,
});

export const useImpersonation = () => useContext(ImpersonationContext);

export const ImpersonationProvider = ({ children }: { children: ReactNode }) => {
  const [impersonatingAs, setImpersonatingAs] = useState<AppRole | null>(() => {
    const stored = sessionStorage.getItem(IMPERSONATE_ROLE_STORAGE_KEY);
    // Validate against the CURRENT ladder — a stale stored value from a
    // retired role (e.g. 'reviewer', pre-Phase-D) would otherwise restrict the
    // UI while the backend silently ignores the invalid header (mismatch).
    if (stored && !(stored in ROLE_RANK)) {
      sessionStorage.removeItem(IMPERSONATE_ROLE_STORAGE_KEY);
      return null;
    }
    return stored as AppRole | null;
  });

  const startImpersonation = useCallback((role: AppRole) => {
    setImpersonatingAs(role);
    sessionStorage.setItem(IMPERSONATE_ROLE_STORAGE_KEY, role);
  }, []);

  const stopImpersonation = useCallback(() => {
    setImpersonatingAs(null);
    sessionStorage.removeItem(IMPERSONATE_ROLE_STORAGE_KEY);
  }, []);

  return (
    <ImpersonationContext.Provider value={{
      impersonatingAs,
      startImpersonation,
      stopImpersonation,
      isImpersonating: impersonatingAs !== null,
    }}>
      {children}
    </ImpersonationContext.Provider>
  );
};
